import React, { useEffect, useState } from "react";
import MySelector from "../MySelector";
import MyToggleButton from "../MyToggleButton";

const printTypeOptions = ["Digital Print", "Offset Print", "Large Format", "Screen Print", "3D Print"];
const paperSizeOptions = ["A3", "A4", "A5", "A6", "Letter", "Legal", "Custom"];
const paperThicknessOptions = ["80gsm", "100gsm", "120gsm", "160gsm", "200gsm", "250gsm", "300gsm"];
const orientationOptions = ["Portrait", "Landscape"];
const timelineOptions = ["ASAP", "Within 24 hours", "Within 3 days", "Within a week", "No rush"];
const qualityOptions = ["Draft", "Standard", "High Quality", "Premium"];
const quantityOptions = [1, 5, 10, 25, 50, 100, 250, 500, 1000];

const PrintOptions = ({ files, setFiles, onBack, onContinue }) => {
    const [errors, setErrors] = useState({});
    const [selectedFileId, setSelectedFileId] = useState(
        files?.[0]?.id || null
    );

    const selectedFile = files.find(
        (item) => item.id === selectedFileId
    );

    const options = selectedFile?.printOptions || {};


    const updateFileOption = (optionName, value) => {
        setFiles((prev) =>
            prev.map((item) =>
                item.id === selectedFileId
                    ? {
                        ...item,
                        printOptions: {
                            ...item.printOptions,
                            [optionName]: value,
                        },
                    }
                    : item
            )
        );
    };

    useEffect(() => {
        if (files.length > 0 && !selectedFileId) {
            setSelectedFileId(files[0].id);
        }
    }, [files, selectedFileId]);

    const isFileComplete = (file) => {
        const o = file.printOptions;

        return (
            o.printType &&
            o.paperSize &&
            o.quality &&
            o.quantity &&
            o.paperThickness &&
            o.orientation &&
            o.color &&
            o.binding &&
            o.doubleSided &&
            o.printNeedBy
        );
    };

    const allFilesReady =
        files.length > 0 &&
        files.every(isFileComplete);

    return (
        <div className="w-full max-w-[860px] bg-white rounded-[16px] shadow-[0px_2px_28px_0px_rgba(0,0,0,0.07)] p-[24px] md:p-[40px] flex flex-col gap-[28px]">

            {/* Header */}
            <div>
                <h1 className="font-semibold text-[24px] text-[#212326] mb-[6px]">
                    Print options
                </h1>
                <p className="font-medium text-[14px] text-[#717182]">
                    Configure your print preferences. All fields are optional.
                </p>
            </div>

            {files?.length > 0 && (
                <div className="rounded-[14px] border border-[#e4e5e7] bg-[#fafafa] overflow-hidden">
                    <div className="px-[16px] py-[12px] border-b border-[#e4e5e7] flex items-center justify-between">
                        <div>
                            <h3 className="font-semibold text-[15px] text-[#212326]">
                                Uploaded Files
                            </h3>
                            <p className="text-[12px] text-[#717182]">
                                {files.length} file{files.length > 1 ? "s" : ""} selected
                            </p>
                        </div>

                        <span className="text-[12px] font-semibold text-[#ff5323]">
                            Ready to Print
                        </span>
                    </div>

                    <div className="max-h-[220px] overflow-y-auto">
                        {files.map((file, index) => (
                            <button
                                key={index}
                                onClick={() => setSelectedFileId(file.id)}
                                className={`px-[16px] py-[12px] flex w-full items-center gap-[12px]
                                ${selectedFileId === file.id ? "bg-[#fff8f5] border border-[#ff5323] last:rounded-b-[14px]" : "border-b border-[#ececec] last:border-b-0"}`}
                            >
                                <div className="w-[36px] h-[36px] rounded-[10px] bg-white border border-[#e4e5e7] flex items-center justify-center shrink-0">
                                    {file.file.type.startsWith("image/") ? (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="w-[18px] h-[18px] text-[#ff5323]"
                                        >
                                            <rect width="18" height="18" x="3" y="3" rx="2" />
                                            <circle cx="9" cy="9" r="2" />
                                            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                                        </svg>
                                    ) : (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="w-[18px] h-[18px] text-[#ff5323]"
                                        >
                                            <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                                            <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                                            <path d="M8 13h8" />
                                            <path d="M8 17h8" />
                                        </svg>
                                    )}
                                </div>

                                <div className="flex-1 min-w-0 text-left">
                                    <div className="flex items-center justify-between gap-2">
                                        <p className="font-medium text-[13px] text-[#212326] truncate">
                                            {file.file.name}
                                        </p>

                                        <span
                                            className={`shrink-0 px-2 py-1 rounded-full text-[11px] font-semibold
            ${isFileComplete(file)
                                                    ? "bg-green-100 text-green-700"
                                                    : "bg-red-100 text-red-600"
                                                }`}
                                        >
                                            {isFileComplete(file)
                                                ? "Details Added"
                                                : "Details Missing"}
                                        </span>
                                    </div>

                                    <p className="text-[12px] text-[#9298a0]">
                                        {(file.file.size / 1024 / 1024).toFixed(2)} MB
                                    </p>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}


            <div className='flex flex-col gap-[20px] w-full'>

                <div className='flex flex-wrap gap-[16px]'>
                    <div className='flex flex-col gap-[8px] flex-1 min-w-[160px]'>
                        <p className='text-textBlack font-semibold font-14'>Print Type</p>
                        <MySelector
                            options={printTypeOptions}
                            placeholder="Select Print Type"
                            disabledOptions={["Building Plans", "Canvas", "Flyer", "Poster", "Custom"]}
                            handleSaveState={(val) => updateFileOption('printType', val)}
                            error={errors.printType}
                            defaultOption={options.printType}
                        />

                    </div>
                    <div className='flex flex-col gap-[8px] flex-1 min-w-[160px]'>
                        <p className='text-textBlack font-semibold font-14'>Paper Size</p>
                        <MySelector
                            options={paperSizeOptions}
                            placeholder="Select Paper Size"
                            // disabledOptions={printType === "Building Plans" ? ["A3", "A4"] : ["A0"]}
                            handleSaveState={(val) => updateFileOption('paperSize', val)}
                            error={errors.paperSize}
                            defaultOption={options.paperSize}
                        />

                    </div>
                    <div className='flex flex-col gap-[8px] flex-1 min-w-[160px]'>
                        <p className='text-textBlack font-semibold font-14'>Quality</p>
                        <MySelector
                            options={qualityOptions}
                            placeholder="Select Quality"
                            handleSaveState={(val) => updateFileOption('quality', val)}
                            error={errors.quality}
                            defaultOption={options.quality}
                        />
                    </div>
                    <div className='flex flex-col gap-[8px] flex-1 min-w-[160px]'>
                        <p className='text-textBlack font-semibold font-14'>Quantity</p>
                        <MySelector
                            options={quantityOptions}
                            placeholder="Select Quantity"
                            handleSaveState={(val) => updateFileOption('quantity', val)}
                            error={errors.quantity}
                            defaultOption={options.quantity}
                        />
                    </div>

                </div>
                <div className='flex flex-col gap-[8px]'>
                    <div className='flex flex-col gap-[8px] flex-1 min-w-[160px]'>
                        <p className='text-textBlack font-semibold font-14'>When</p>
                        <MySelector
                            options={timelineOptions}
                            placeholder="Select Timeline"
                            handleSaveState={(val) => updateFileOption('printNeedBy', val)}
                            error={errors.timeline}
                            defaultOption={options.printNeedBy}
                        />
                    </div>
                </div>
                <div className='flex flex-col md:flex-row md:items-center justify-between gap-3 mt-3'>
                    <div className='flex flex-col gap-[8px] flex-1 min-w-[160px]'>
                        <p className='text-textBlack font-semibold font-14'>Paper Thickness</p>
                        <MySelector
                            options={paperThicknessOptions}
                            placeholder="Select Thickness"
                            handleSaveState={(val) => updateFileOption('paperThickness', val)}
                            error={errors.paperThickness}
                            defaultOption={options.paperThickness}
                        />
                    </div>

                    <div className='flex flex-col gap-[8px] flex-1 min-w-[160px]'>
                        <p className='text-textBlack font-semibold font-14'>Orientation</p>
                        <MySelector
                            options={orientationOptions}
                            placeholder="Select Orientation"
                            handleSaveState={(val) => updateFileOption('orientation', val)}
                            error={errors.orientation}
                            defaultOption={options.orientation}
                        />
                    </div>
                    <div className='flex flex-col gap-[8px] flex-1'>
                        <p className='text-textBlack font-semibold font-14'>Color</p>
                        <MyToggleButton
                            type="Color"
                            handleSaveState={(val) => updateFileOption('color', val)}
                            disableYes={false}
                            error={errors.color}
                            defaultSelection={options.color}
                        />
                    </div>
                    <div className='flex flex-col gap-[8px] flex-1'>
                        <p className='text-textBlack font-semibold font-14'>Binding</p>
                        <MyToggleButton
                            type="Binding"
                            handleSaveState={(val) => updateFileOption('binding', val)}
                            disableYes={false}
                            error={errors.binding}
                            defaultSelection={options.binding}
                        />
                    </div>
                    <div className='flex flex-col gap-[8px] flex-1'>
                        <p className='text-textBlack font-semibold font-14'>Double Sided</p>
                        <MyToggleButton
                            type="Double Sided"
                            handleSaveState={(val) => updateFileOption('doubleSided', val)}
                            disableYes={false}
                            error={errors.doubleSided}
                            defaultSelection={options.doubleSided}
                        />
                    </div>
                </div>
                <div className="flex flex-col gap-[8px]">
                    <p className="font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[14px] text-[#212326]">Additional Notes</p>
                    <textarea
                        value={options.additionalNotes}
                        onChange={(e) => updateFileOption('additionalNotes', e.target.value)}
                        placeholder="Any special instructions for the print shop..."
                        className="w-full h-[80px] rounded-[12px] border border-[#e4e5e7] px-[16px] py-[12px] font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[14px] text-[#212326] placeholder:text-[#9298a0] resize-none focus:outline-none focus:border-[#ff5323] transition-colors"
                    ></textarea>
                </div>
            </div>

            {/* Footer */}
            <div className="flex flex-col-reverse md:flex-row md:justify-between gap-[10px]">
                <button
                    className="flex items-center justify-center gap-[8px] w-full md:w-auto px-[20px] py-[12px] rounded-[10px] border border-[#e4e5e7] font-semibold text-[15px] text-[#212326] hover:bg-[#f4f5f6] transition-colors"
                    onClick={onBack}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-left w-[16px] h-[16px]">
                        <path d="m15 18-6-6 6-6"></path>
                    </svg>
                    Back
                </button>

                <button
                    disabled={!allFilesReady}
                    onClick={onContinue}
                    className={`flex items-center justify-center gap-[8px] w-full md:w-auto px-[20px] md:px-[28px] py-[12px] rounded-[10px] font-semibold text-[15px] transition-all
        ${allFilesReady
                            ? "bg-[#ff5323] text-white hover:bg-[#e64a1f]"
                            : "bg-[#e4e5e7] text-[#9298a0] cursor-not-allowed"
                        }`}
                >
                    Continue
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-right w-[16px] h-[16px]">
                        <path d="M5 12h14"></path>
                        <path d="m12 5 7 7-7 7"></path>
                    </svg>
                </button>
            </div>

        </div>
    );
};

export default PrintOptions;